/* VPSPOD-462 — CEI Florida General Contractor PLP behaviour.
   No dependencies. Every feature degrades to usable static markup if this
   file fails to load: tabs render all panels, the matrix renders expanded,
   and the sticky bar simply stays hidden. */
(function () {
  'use strict';

  /* Every section that renders part of this page emits its own
     <script src="gc-plp.js">, and the browser executes each tag separately -
     six times on the current template. Most features survived that because
     their handlers set state explicitly (tabs) or keep it in a closure (the
     dialog, six copies moving in lockstep). The disclosure toggle did not: it
     reads aria-expanded off the DOM and flips it, so six handlers flipped it
     six times and cancelled out - "Compare every feature" did nothing at all.
     Binding once per element fixes that and drops five redundant
     IntersectionObservers at the same time.

     Keyed per element rather than with one global flag, so a section
     re-rendered in the theme editor still binds its fresh DOM. */
  function bindOnce(el, key) {
    var flag = 'gcBound' + key;
    if (el.dataset[flag]) return false;
    el.dataset[flag] = '1';
    return true;
  }

  /* ---------------------------------------------------------------- tabs */
  function initTabs(root) {
    var list = root.querySelector('[data-gc-tablist]');
    if (!list) return;
    var tabs = Array.prototype.slice.call(list.querySelectorAll('[role="tab"]'));
    if (!tabs.length) return;
    if (!bindOnce(list, 'Tabs')) return;

    function select(index, focus) {
      tabs.forEach(function (tab, i) {
        var selected = i === index;
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.setAttribute('tabindex', selected ? '0' : '-1');
        var panel = root.querySelector('#' + tab.getAttribute('aria-controls'));
        if (panel) panel.hidden = !selected;
      });
      if (focus) tabs[index].focus();
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { select(i, false); });
      tab.addEventListener('keydown', function (e) {
        var next = null;
        if (e.key === 'ArrowRight') next = (i + 1) % tabs.length;
        else if (e.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = tabs.length - 1;
        if (next !== null) { e.preventDefault(); select(next, true); }
      });
    });

    // JS is available, so collapse to the default tab.
    var initial = tabs.findIndex(function (t) { return t.getAttribute('aria-selected') === 'true'; });
    select(initial < 0 ? 0 : initial, false);
  }

  /* ------------------------------------------------- comparison matrix */
  function initDisclosure(root) {
    var toggles = root.querySelectorAll('[data-gc-toggle]');
    Array.prototype.forEach.call(toggles, function (toggle) {
      var panel = root.querySelector('#' + toggle.getAttribute('aria-controls'));
      if (!panel) return;
      if (!bindOnce(toggle, 'Disclosure')) return;
      // Collapse now that JS can expand it again.
      panel.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      toggle.hidden = false;
      toggle.addEventListener('click', function () {
        var open = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
        panel.hidden = open;
        var labelEl = toggle.querySelector('[data-gc-toggle-label]');
        if (labelEl) {
          var next = open ? toggle.getAttribute('data-label-closed') : toggle.getAttribute('data-label-open');
          if (next) labelEl.textContent = next;
        }
      });
    });
  }

  /* ------------------------------------------- sub-nav active section */
  function initSubnav(root) {
    var links = Array.prototype.slice.call(root.querySelectorAll('[data-gc-anchor]'));
    if (!links.length || !('IntersectionObserver' in window)) return;
    if (!bindOnce(root, 'Subnav')) return;

    var byId = {};
    var targets = [];
    links.forEach(function (link) {
      var id = link.getAttribute('href').replace(/^#/, '');
      var el = document.getElementById(id);
      if (el) { byId[id] = link; targets.push(el); }
    });
    if (!targets.length) return;

    var visible = {};
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        visible[entry.target.id] = entry.isIntersecting;
      });
      var current = null;
      for (var i = 0; i < targets.length; i++) {
        if (visible[targets[i].id]) { current = targets[i].id; break; }
      }
      links.forEach(function (link) { link.removeAttribute('aria-current'); });
      if (current && byId[current]) byId[current].setAttribute('aria-current', 'true');
    }, { rootMargin: '-30% 0px -60% 0px', threshold: 0 });

    targets.forEach(function (t) { observer.observe(t); });
  }

  /* -------------------------------------------------- sticky mobile CTA */
  function initStickyCta() {
    var bar = document.querySelector('[data-gc-sticky-cta]');
    if (!bar) return;
    if (!bindOnce(bar, 'Sticky')) return;

    /* Two ways to drive the bar. `data-after` names one section and the bar
       shows for the whole rest of the page once that section is behind you -
       what Lucas asked for on 2026-08-20, since `data-watch` hid the bar
       through the entire middle of the page. `data-watch` is kept for any
       template still configured that way. */
    var afterEl = document.getElementById(bar.getAttribute('data-after') || '');

    var watchIds = (bar.getAttribute('data-watch') || '').split(',')
      .map(function (s) { return s.trim(); })
      .filter(Boolean);
    var watched = watchIds.map(function (id) { return document.getElementById(id); }).filter(Boolean);

    /* Reserve room at the END of the page so the fixed bar cannot cover the
       footer - measured, near the bottom, at ~75px of overlap.

       This was document.querySelector('.gc-plp'), which returns the FIRST
       wrapper on the page: the sub-nav. So the reserve landed as 72px of blank
       white space between the sub-nav and the hero, and the footer it was
       meant to protect got nothing. Every section wraps itself in .gc-plp, so
       the last one is the end of the page.

       The height is published as a custom property rather than hardcoded in
       CSS: the bar grows when its label wraps or the type scale changes, and a
       stale constant is what let this go unnoticed. */
    var wrappers = document.querySelectorAll('.gc-plp');
    var wrapper = wrappers.length ? wrappers[wrappers.length - 1] : null;
    if (wrapper) {
      wrapper.classList.add('gc-plp--has-sticky-cta');
      wrapper.style.setProperty('--gc-sticky-cta-h', bar.offsetHeight + 'px');
    }

    if (!('IntersectionObserver' in window)) return;

    if (afterEl) {
      /* Reveal once EITHER the watched section is fully behind you OR you have
         scrolled a whole screen - whichever comes first.

         The section test alone is not enough on mobile: the hero is ~1423px
         tall on a 390x844 screen, so waiting for all of it to clear the top
         meant the button did not appear until ~1723px of scrolling, roughly
         two full screens. The one-viewport rule bounds that at a single screen
         while the section test still handles a short hero on a tall screen.

         A rAF-throttled passive scroll listener rather than an
         IntersectionObserver: IO only fires on threshold crossings, so
         "partway past a section taller than the viewport" is exactly the case
         it cannot express. This is desktop-hidden by CSS, so the listener only
         ever matters on mobile. */
      var update = function () {
        var past = afterEl.getBoundingClientRect().bottom <= 0;
        var y = window.scrollY || document.documentElement.scrollTop || 0;
        bar.classList.toggle('is-visible', past || y >= window.innerHeight);
      };

      var queued = false;
      var onScroll = function () {
        if (queued) return;
        queued = true;
        requestAnimationFrame(function () {
          queued = false;
          update();
        });
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      update();
      return;
    }

    if (!watched.length) return;

    var state = {};
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) { state[entry.target.id] = entry.isIntersecting; });
      var anyVisible = Object.keys(state).some(function (k) { return state[k]; });
      bar.classList.toggle('is-visible', anyVisible);
    }, { threshold: 0 });

    watched.forEach(function (el) { observer.observe(el); });
  }

  /* ------------------------------------------------- FAQ show all/less --- */
  /* The overflow questions ship in the DOM and are revealed in place, so the
     reader is never navigated away and search engines still see every answer.
     Not initDisclosure(): that hides one whole panel, whereas here the extra
     rows live inside the same accordion list as the visible ones. */
  function initFaqMore(root) {
    var btn = root.querySelector('[data-gc-faq-more]');
    if (!btn) return;

    var list = root.querySelector('[data-gc-faq-list]');
    if (!list) return;
    if (!bindOnce(btn, 'FaqMore')) return;

    var labelEl = btn.querySelector('[data-gc-faq-more-label]');

    function paint(expanded) {
      list.classList.toggle('is-expanded', expanded);
      btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      if (labelEl) {
        var next = expanded
          ? btn.getAttribute('data-label-open')
          : btn.getAttribute('data-label-closed');
        if (next) labelEl.textContent = next;
      }
    }

    paint(false);

    btn.addEventListener('click', function () {
      var expanded = btn.getAttribute('aria-expanded') === 'true';
      paint(!expanded);

      if (expanded) {
        /* Collapsing: close any answer the reader opened down in the hidden
           range, so expanding again does not come back mid-scroll with rows
           already open. */
        Array.prototype.forEach.call(list.querySelectorAll('.gc-faq__extra details[open]'), function (d) {
          d.open = false;
        });
      }
    });
  }

  /* --------------------------------------------- Pass Promise tooltip ---- */
  /* Replaces a title= attribute, which browsers show inconsistently on
     desktop and not at all on touch. The button carries the label, so the
     text itself is no longer an affordance. */
  function initTip(root) {
    var tip = root.querySelector('[data-gc-tip]');
    if (!tip) return;

    var btn = tip.querySelector('[data-gc-tip-btn]');
    var bubble = tip.querySelector('[data-gc-tip-bubble]');
    if (!btn || !bubble) return;
    if (!bindOnce(tip, 'Tip')) return;

    function setOpen(open) {
      bubble.hidden = !open;
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    setOpen(false);

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      setOpen(bubble.hidden);
    });

    // Escape and any click elsewhere dismiss it, so it cannot be left open
    // covering the hero copy.
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !bubble.hidden) {
        setOpen(false);
        btn.focus({ preventScroll: true });
      }
    });

    document.addEventListener('click', function (e) {
      if (!bubble.hidden && !tip.contains(e.target)) setOpen(false);
    });
  }

  /* ------------------------------------------- license-path guidance dialog */
  function initQualification() {
    var dialog = document.getElementById('gc-qual-dialog');
    if (!dialog) return;

    var steps = Array.prototype.slice.call(dialog.querySelectorAll('[data-gc-qual-step]'));
    var outcome = dialog.querySelector('[data-gc-qual-outcome]');
    var body = dialog.querySelector('.gc-qual__body');
    if (!steps.length || !outcome) return;
    if (!bindOnce(dialog, 'Qual')) return;

    // Answers are kept here only, for the life of the panel. Nothing is
    // persisted, submitted or reported anywhere.
    var answers = [];
    var index = 0;
    var lastTrigger = null;

    function paint() {
      steps.forEach(function (step, i) {
        step.hidden = !(i === index);
        Array.prototype.forEach.call(step.querySelectorAll('[data-gc-qual-option]'), function (btn) {
          btn.setAttribute('aria-pressed', btn.textContent.trim() === answers[i] ? 'true' : 'false');
        });
      });
      outcome.hidden = index < steps.length;
      // Only move focus while the panel is actually open, otherwise the
      // initial paint on page load would steal focus and jump the page.
      if (!dialog.open) return;
      if (body) body.scrollTop = 0;
      var visible = index < steps.length ? steps[index] : outcome;
      var focusTarget = visible.querySelector('[data-gc-qual-option], [data-gc-qual-back]');
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    }

    function open(trigger) {
      lastTrigger = trigger || document.activeElement;
      answers = [];
      index = 0;
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      document.documentElement.classList.add('gc-qual-open');
      paint();
    }

    function close() {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    }

    dialog.addEventListener('close', function () {
      document.documentElement.classList.remove('gc-qual-open');
      // A native <dialog> hands focus back to whatever was focused when
      // showModal() ran. A real click focuses the trigger, so that usually
      // works - but not if the panel was opened programmatically (a scripted
      // click, a deep link), where focus would land on <body> and the user
      // would be dropped at the top of the page. Restoring the trigger
      // explicitly makes the return unconditional.
      if (lastTrigger && document.contains(lastTrigger)) {
        lastTrigger.focus({ preventScroll: true });
      }
    });

    // Triggers stay real links; intercept only when the dialog is available.
    Array.prototype.forEach.call(document.querySelectorAll('[data-gc-qualify]'), function (trigger) {
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        open(trigger);
      });
    });

    dialog.addEventListener('click', function (e) {
      if (e.target.closest('[data-gc-qual-close]')) { close(); return; }
      if (e.target.closest('[data-gc-qual-option]')) {
        var btn = e.target.closest('[data-gc-qual-option]');
        answers[index] = btn.textContent.trim();
        index = Math.min(index + 1, steps.length);
        paint();
        return;
      }
      if (e.target.closest('[data-gc-qual-back]')) {
        index = Math.max(index - 1, 0);
        paint();
        return;
      }
      var primary = e.target.closest('[data-gc-qual-primary]');
      if (primary) {
        var id = primary.getAttribute('data-target');
        close();
        var target = id && document.getElementById(id);
        if (target) {
          requestAnimationFrame(function () {
            target.scrollIntoView({
              behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
              block: 'start',
            });
          });
        }
        return;
      }
      // Click on the backdrop area outside the panel closes the sheet.
      if (e.target === dialog) close();
    });

    paint();
  }

  function init() {
    var roots = document.querySelectorAll('[data-gc-section]');
    Array.prototype.forEach.call(roots, function (root) {
      initTabs(root);
      initDisclosure(root);
      initSubnav(root);
      initTip(root);
      initFaqMore(root);
    });
    initStickyCta();
    initQualification();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run inside the theme editor when a section is re-rendered.
  document.addEventListener('shopify:section:load', init);
})();
